import { Box, Button, Flex, Input, InputGroup, InputLeftElement, Text } from '@chakra-ui/react';
import { ReactComponent as CopyLinkIcon } from 'assets/icons/share_link.svg';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { store, useAppSelector } from 'modules/Editor/store';
import { selectPageCount } from 'modules/Editor/store/infographSelector';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { WidgetsMap } from 'types/infographTypes';
import { WidgetType } from 'types/widget.types';
import { isStandalone } from 'utils/environment';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';

import { ResponsiveLinkPreviewModal } from '../../components/ResponsiveLinkPreview/ResponsiveLinkPreviewModal';

type LinkSharingProps = {
  disabled?: boolean;
  onCopyLink: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
};

const UNSUPPORTED_CATEGORIES = ['diagrams', 'mind maps'];
const UNSUPPORTED_WIDGETS = [WidgetType.StatChart, WidgetType.MultipleChoices];
/**
 * Determines if the responsive link should be disabled based on the given parameters.
 * Will be adjusted as we add more support for different widgets/categories.
 *
 * @param widgets - The map of widgets.
 * @param pageCount - The number of pages.
 * @param templateCategory - The category of the template.
 * @returns A boolean indicating whether the responsive link should be disabled.
 */
const isResponsiveLinkDisabled = (widgets: WidgetsMap, pageCount: number, templateCategory: string) => {
  // disabled for multiple pages
  if (pageCount > 1) return true;

  // disabled for specific categories
  if (templateCategory && UNSUPPORTED_CATEGORIES.includes(templateCategory)) return true;

  for (const widgetId in widgets) {
    const widgetType = getWidgetTypeFromId(widgetId);
    if (UNSUPPORTED_WIDGETS.includes(widgetType)) {
      return true;
    }
  }

  return false;
};

const responsiveLinkTestId = 'responsive-link-sharing';
const sharePrivateLinkButtonTestId = 'share-responsive-link-copy-link-button';

export const ShareResponsiveLink = ({ disabled = false, onCopyLink }: LinkSharingProps) => {
  const { t } = useTranslation('common_navbar');
  const { venngageE2 } = useVenngageE1Gate();

  const pageCount = useAppSelector(selectPageCount);
  const templateCategory = venngageE2?.templateCategory?.category || '';

  if (disabled) return null;
  if (isResponsiveLinkDisabled(store.getState().infograph.widgets, pageCount, templateCategory)) return null;

  const title = 'Secure Site';
  const label = 'Adjusts your content to fit the screen size.';

  return (
    <Flex flexDir='column' gap='2' data-testid={responsiveLinkTestId}>
      <Flex flexDir='column'>
        <Text fontWeight='semibold' fontSize='sm' lineHeight='1.25' color={'font.500'}>
          {title}
        </Text>
        <Flex flexDir='row' justifyContent='space-between' alignItems='center' my='2'>
          <Flex direction={'column'} color='font.300'>
            <Text fontSize='xs' lineHeight='1.43'>
              {label}
            </Text>
          </Flex>
          <Flex gap='8px'>
            <ResponsiveLinkPreviewModal />
            <Button
              onClick={onCopyLink}
              variant='action'
              size='sm'
              width='28'
              leftIcon={<CopyLinkIcon style={{ marginRight: 1 }} />}
              data-testid={sharePrivateLinkButtonTestId}
            >
              {t('modals.share-panel.copy-link')}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

const shareResponsiveLinkTestId = 'share-responsive-link';
const shareResponsiveLinkCopyInputTestId = 'share-responsive-link-copy-link-input';

export const ShareResponsiveLinkInput = ({ disabled = false, onCopyLink = () => {} }: LinkSharingProps) => {
  const { t } = useTranslation('common_navbar');
  const { id } = useParams();
  const { venngageE2 } = useVenngageE1Gate();
  const pageCount = useAppSelector(selectPageCount);

  const privateLinkURL = useMemo(() => {
    const shareLinkId = isStandalone ? id : venngageE2?.privateLinkId;
    const fakeId = '123456abc'; // This fake ID is the same with the e1
    const url = new URL(`${window.location.origin}/rl/${disabled ? fakeId : shareLinkId}`).toString();

    return url;
  }, [disabled, id, venngageE2]);

  if (disabled) return null;

  const templateCategory = venngageE2?.templateCategory?.category || '';
  if (isResponsiveLinkDisabled(store.getState().infograph.widgets, pageCount, templateCategory)) return null;

  const title = 'Secure Site';

  return (
    <Flex flexDir='column' gap='2' data-testid={shareResponsiveLinkTestId}>
      <Text fontWeight={'semibold'} fontSize='sm' lineHeight='1.25' color={'font.500'}>
        {title}
      </Text>
      <Flex gap='2'>
        <Box flexGrow={1}>
          <InputGroup>
            <InputLeftElement pointerEvents='none' pl='2' width='32px' height='32px' color='font.300'>
              <CopyLinkIcon width='20px' height='20px' />
            </InputLeftElement>
            <Input
              isReadOnly
              size='sm'
              textOverflow={'ellipsis'}
              disabled={disabled}
              value={privateLinkURL}
              data-testid={shareResponsiveLinkCopyInputTestId}
              borderRadius={'base'}
            />
          </InputGroup>
        </Box>
        <Flex gap='8px'>
          <ResponsiveLinkPreviewModal />
          <Button
            onClick={onCopyLink}
            disabled={disabled}
            variant='action'
            size='sm'
            width='24'
            data-testid={sharePrivateLinkButtonTestId}
          >
            {t('modals.share-panel.copy-link')}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
